import { motion } from "framer-motion";
import { Megaphone, Sparkles, Star, TrendingUp, Eye, Users, MessageCircle, Phone, ArrowRight } from "lucide-react";

const float = (delay, y = 10) => ({
  animate: { y: [0, -y, 0] },
  transition: { duration: 3 + delay, repeat: Infinity, ease: "easeInOut", delay },
});

function PromoIllustration() {
  return (
    <div className="relative h-72 sm:h-80 w-full max-w-md mx-auto" data-testid="promo-illustration">
      <motion.div className="absolute inset-6 rounded-full bg-[#E5A93C]/20" animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute inset-14 rounded-full bg-[#B85C38]/15" animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.4 }} />

      {/* Billboard */}
      <motion.div initial={{ opacity: 0, y: 30, rotate: -4 }} animate={{ opacity: 1, y: 0, rotate: -4 }} transition={{ duration: 0.7 }}
        className="absolute left-1/2 top-8 -translate-x-1/2 w-56 sm:w-64 bg-white rounded-2xl border-4 border-[#4A2E2B] shadow-xl p-3">
        <div className="text-[9px] uppercase tracking-widest font-bold text-[#8C827A]">Slot Iklan Anda</div>
        <div className="mt-1.5 h-3 rounded bg-[#E5A93C] w-4/5" />
        <div className="mt-1.5 h-2 rounded bg-[#E5DFD7] w-full" />
        <div className="mt-1 h-2 rounded bg-[#E5DFD7] w-3/4" />
        <div className="mt-3 h-14 rounded-lg bg-gradient-to-br from-[#4A2E2B] to-[#B85C38] flex items-center justify-center">
          <motion.span className="text-white text-xs font-extrabold tracking-wide" animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
            LOGO / BRAND ANDA
          </motion.span>
        </div>
      </motion.div>
      <div className="absolute left-1/2 -translate-x-1/2 top-[196px] sm:top-[214px] w-2 h-16 bg-[#4A2E2B] rounded-b" />

      {/* Megaphone */}
      <motion.div {...float(0, 8)} className="absolute left-4 sm:left-8 bottom-8 h-20 w-20 rounded-2xl bg-[#4A2E2B] shadow-xl flex items-center justify-center rotate-[-12deg]">
        <Megaphone className="h-10 w-10 text-[#E5A93C]" />
      </motion.div>
      {[0, 1, 2].map((i) => (
        <motion.div key={i} className="absolute left-24 sm:left-28 bottom-16 h-2 w-2 rounded-full bg-[#E5A93C]"
          animate={{ x: [0, 40 + i * 14], y: [0, -14 - i * 10], opacity: [1, 0], scale: [1, 1.6] }}
          transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.45, ease: "easeOut" }} />
      ))}

      {/* Floating badges */}
      <motion.div {...float(0.6)} className="absolute right-4 sm:right-8 top-4 bg-white rounded-xl shadow-lg border border-[#E5DFD7] px-3 py-2 flex items-center gap-2">
        <Eye className="h-4 w-4 text-[#B85C38]" /><span className="text-xs font-bold text-[#1F1917]">Ribuan Pengunjung</span>
      </motion.div>
      <motion.div {...float(1.1)} className="absolute right-2 sm:right-4 bottom-10 bg-white rounded-xl shadow-lg border border-[#E5DFD7] px-3 py-2 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-emerald-600" /><span className="text-xs font-bold text-[#1F1917]">Jangkauan se-Depok</span>
      </motion.div>
      <motion.div className="absolute left-10 top-6 text-[#E5A93C]" animate={{ rotate: [0, 20, 0], scale: [1, 1.2, 1] }} transition={{ duration: 2.4, repeat: Infinity }}>
        <Sparkles className="h-6 w-6" />
      </motion.div>
      <motion.div className="absolute right-20 bottom-24 text-[#B85C38]" animate={{ rotate: [0, -25, 0], scale: [1, 1.3, 1] }} transition={{ duration: 3, repeat: Infinity, delay: 0.8 }}>
        <Star className="h-5 w-5 fill-current" />
      </motion.div>
    </div>
  );
}

export function PromoSection() {
  const perks = [
    { icon: Users, text: "Dilihat puluhan ribu pramuka, pembina, dan orang tua se-Kota Depok" },
    { icon: Eye, text: "Tampil di halaman utama Pusat Data resmi Kwarcab Depok" },
    { icon: TrendingUp, text: "Dukung kegiatan kepramukaan sekaligus perkuat citra brand Anda" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="landing-promo-section">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="relative overflow-hidden rounded-[2rem] bg-white border border-[#E5DFD7] shadow-sm grid lg:grid-cols-2 gap-6 p-6 sm:p-10">
        <div className="absolute -top-24 -left-24 h-64 w-64 rounded-full bg-[#E5A93C]/10" />
        <div className="relative flex flex-col justify-center">
          <div className="inline-flex items-center gap-2 self-start bg-[#E5A93C]/15 text-[#8a5a10] px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
            <Megaphone className="h-3.5 w-3.5" /> Ruang Promosi
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1F1917] leading-tight">
            Pasang Iklan Anda di <span className="text-[#B85C38]">SIPRAM DEPOK</span>
          </h2>
          <p className="mt-4 text-[#4A423F] leading-relaxed max-w-lg">
            Jadikan brand, sekolah, toko perlengkapan pramuka, atau kegiatan Anda dikenal oleh komunitas Pramuka Kota Depok. Ruang ini terbuka untuk mitra dan sponsor.
          </p>
          <ul className="mt-6 space-y-3">
            {perks.map((p) => {
              const Icon = p.icon;
              return (
                <li key={p.text} className="flex items-start gap-3 text-sm text-[#4A423F]">
                  <span className="h-7 w-7 rounded-lg bg-[#4A2E2B] flex items-center justify-center shrink-0"><Icon className="h-3.5 w-3.5 text-[#E5A93C]" /></span>
                  {p.text}
                </li>
              );
            })}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="https://wa.me/6289650041023?text=Halo%20PUSDATIN%20Pramuka%20Kota%20Depok%2C%20saya%20ingin%20mengajukan%20iklan%20di%20SIPRAM%20DEPOK." target="_blank" rel="noreferrer" data-testid="promo-cta-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3.5 rounded-full font-bold hover:bg-[#1eb85a] transition-colors">
              <MessageCircle className="h-4 w-4" /> Ajukan Iklan via WhatsApp <ArrowRight className="h-4 w-4" />
            </a>
            <a href="tel:+6289650041023" data-testid="promo-phone-link"
              className="inline-flex items-center gap-2 bg-white border border-[#E5DFD7] text-[#4A2E2B] px-6 py-3.5 rounded-full font-bold hover:bg-[#F7F4EF] transition-colors">
              <Phone className="h-4 w-4 text-[#B85C38]" /> 0896-5004-1023
            </a>
          </div>
        </div>
        <div className="relative flex items-center">
          <PromoIllustration />
        </div>
      </motion.div>
    </section>
  );
}
